import React, { useState, useEffect } from "react";
import { FixedSizeGrid } from "react-window";
import { useTheme } from "@mui/material/styles";
import ImageSkeleton from "../components/ImageSkeleton";

export const ImageItem = ({
  image,
  isSelected,
  isSelectedLocked,
  onSelect,
  selectsMade,
}) => {
  const theme = useTheme();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const backgroundColor =
    theme.palette.mode === "light" ? "#f5f5f5" : "#353535";

  const containerStyle = {
    width: "100%",
    height: "100%",
    backgroundColor: backgroundColor,
    position: "relative",
    borderRadius: "4px",
    outline: isSelected
      ? "4px solid #4CAF50"
      : isSelectedLocked
      ? "4px solid #3C3CD2"
      : "none",
    cursor: selectsMade
      ? "default"
      : isSelectedLocked
      ? "not-allowed"
      : "pointer",
    backgroundImage:
      isLoaded && image.thumbnailUrl
        ? `url(data:image/jpeg;base64,${image.thumbnailUrl})`
        : isLoaded && image.directUrl
        ? `url(${image.directUrl})`
        : "none",
    backgroundPosition: "center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    transform: isHovered && !selectsMade && !isSelectedLocked ? "scale(1.01)" : "scale(0.99)",
    transition: "all 0.15s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    overflow: "hidden",
  };

  useEffect(() => {
    if (image.thumbnailUrl) {
      console.log(`🖼️ Loading thumbnail for ${image.name}`);
      const img = new Image();
      img.src = `data:image/jpeg;base64,${image.thumbnailUrl}`;
      img.onload = () => {
        console.log(`✅ Thumbnail loaded for ${image.name}`);
        setIsLoaded(true);
      };
      img.onerror = (e) => {
        console.error(`❌ Thumbnail failed for ${image.name}:`, e);
      };
    } else if (image.directUrl) {
      console.log(`🔗 Loading direct URL for ${image.name}:`, image.directUrl);
      const img = new Image();
      img.src = image.directUrl;
      img.onload = () => {
        console.log(`✅ Direct URL loaded for ${image.name}`);
        setIsLoaded(true);
      };
      img.onerror = (e) => {
        console.error(`❌ Direct URL failed for ${image.name}:`, e);
      };
    } else {
      console.log(`⏳ No URL available yet for ${image.name}`);
      setIsLoaded(false);
    }
  }, [image.thumbnailUrl, image.directUrl, image.name]);

  return (
    <div
      id={`image-${image.path_lower}`}
      style={containerStyle}
      onClick={() => {
        if (!isSelectedLocked && !selectsMade) {
          onSelect(image);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {(!isLoaded || (!image.thumbnailUrl && !image.directUrl)) && <LoadingPlaceholder />}
      {isSelected && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "#4CAF50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            animation: "checkmarkBounce 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      )}
    </div>
  );
};

const LoadingPlaceholder = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "4px",
      overflow: "hidden"
    }}
  >
    <ImageSkeleton
      width="100%"
      height="100%"
      rounded="md"
      className="bg-gray-700"
    />
  </div>
);

const Cell = ({ columnIndex, rowIndex, style, data }) => {
  const {
    images,
    selectedImages,
    lockedSelectedImages,
    onImageSelect,
    columnCount,
    selectsMade,
  } = data;
  const index = rowIndex * columnCount + columnIndex;
  const image = images[index];

  if (!image) return null;

  return (
    <div style={{ ...style, padding: 4 }}>
      <ImageItem
        image={image}
        isSelected={selectedImages.some((img) => img.name === image.name)}
        isSelectedLocked={lockedSelectedImages.some(
          (img) => img.name === image.name
        )}
        onSelect={onImageSelect}
        selectsMade={selectsMade}
      />
    </div>
  );
};

export const GridInAccordion = ({
  images,
  selectedImages,
  lockedSelectedImages,
  onImageSelect,
  gridColumns,
  dimensions,
  itemSize,
  selectsMade,
}) => {
  const containerRef = React.useRef(null);
  const [gridWidth, setGridWidth] = React.useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setGridWidth(rect.width);
      }
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    updateWidth();
    return () => resizeObserver.disconnect();
  }, []);

  const padding = 16;
  const computedWidth = gridWidth - padding;
  const columnWidth = Math.floor(computedWidth / gridColumns);
  const rowCount = Math.ceil(images.length / gridColumns);
  const totalHeight = rowCount * columnWidth;

  return (
    <div ref={containerRef} style={{ width: "100%", overflowX: "hidden" }}>
      {gridWidth > 0 && (
        <FixedSizeGrid
          columnCount={gridColumns}
          columnWidth={columnWidth}
          height={totalHeight}
          rowCount={rowCount}
          rowHeight={columnWidth}
          width={computedWidth}
          itemData={{
            images,
            selectedImages,
            lockedSelectedImages,
            onImageSelect,
            columnCount: gridColumns,
            selectsMade,
          }}
        >
          {Cell}
        </FixedSizeGrid>
      )}
    </div>
  );
};
